import { CommonModule } from '@angular/common';
import { 
  Component, Input, Output, EventEmitter, 
  signal, computed, effect, 
  OnInit, 
  Renderer2,
  ElementRef,
  ViewChild,
  AfterViewInit
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfigInput } from '../../interfaces/interfaces';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './input.component.html',
  styleUrl: './input.component.css',
})
export class InputComponent implements OnInit, AfterViewInit {
  private errorDebounceTimer: any;

  @Input() set config(value: ConfigInput) {
    this.configSignal.set({
      label: value.label || 'Item',
      subLabel: value.subLabel || '',
      formName: value.formName,
      placeholder: value.placeholder || 'Ingrese un valor',
      type: value.type || 'text',
      defaultValue: value.defaultValue ?? '',
      enabled: value.enabled ?? true,
      validation: value.validation || null,
      textError: value.textError,
    });
  }

  @Input() timeoutSeconds: number = 8;
  @ViewChild('subtitleLabel') subtitleLabel!: ElementRef;
  @ViewChild('inbox') inbox!: ElementRef;

  configSignal = signal<ConfigInput>({
    label: 'Item',
    subLabel: '',
    placeholder: 'Ingrese un valor',
    type: 'text',
    defaultValue: null,
    enabled: true,
    validation: null,
    textError: 'Este campo es requerido.'
  });

  formattedValue = signal<string>('');

  enabled = computed(() => this.configSignal().enabled);
  currentValue = computed(() => this.configSignal().defaultValue);

  form: FormGroup;
  offTimeOut = signal(false);
  hasWritten = signal(false);
  valueSignal = signal<any>('');
  lastEmittedValue = signal<any>(null);
  private viewInitialized = signal(false);

  @Output() submitValue = new EventEmitter<any>();

  private debounceTimer: any;

  constructor(private renderer: Renderer2) {
    this.form = new FormGroup({
      item: new FormControl('', Validators.compose(this.configSignal().validation ?? []))
    });

    // Habilitar / deshabilitar dinámicamente
    effect(() => {
      const control = this.form.get('item');
      this.enabled() ? control?.enable() : control?.disable();
      
      // Solo aplicar estilos si la vista ya está inicializada
      if (this.enabled()) {
        this.getStyleEnabled();
      } else {
        if(this.configSignal().formName === 'identification'){
        }
        this.getStyleTextDisabled();
      }
    });

    // Cambiar validaciones dinámicamente
    effect(() => {
      const control = this.form.get('item');
      const validations = this.configSignal().validation;
      control?.setValidators(Validators.compose(validations ?? []));
      control?.updateValueAndValidity();
    });

    // Setear valor por defecto dinámicamente
    effect(() => {
      const control = this.form.get('item');
      if (this.currentValue() !== undefined && control?.value !== this.currentValue()) {
        control?.setValue(this.currentValue());
        if(!this.currentValue() || this.currentValue() === ''){
          this.emit();
          this.startCountdown().then((timeoutDone) => {
            if (timeoutDone) {
              this.offTimeOut.set(true);
              this.getStyleError();
            }
          });
        }
      }
    });

    // Escuchar cambios del valor y activar el valueSignal
    this.form.get('item')?.valueChanges.subscribe((val) => {
      // Si es tipo número, formatear el valor
      if (this.configSignal().type === 'number' && val !== null && val !== '') {
        const numericValue = val.toString().replace(/,/g, '');
        if (!isNaN(numericValue)) {
          // Formatear con separadores de miles
          const formatted = this.formatNumberWithCommas(numericValue);
          this.formattedValue.set(formatted);
          
          setTimeout(() => {
            this.form.get('item')?.setValue(numericValue, { emitEvent: false });
          });
          
          this.valueSignal.set(numericValue);
        }
      } else {
        this.valueSignal.set(val);
        this.formattedValue.set(val);
      }
      
      if (val !== '' && val !== null && val !== undefined) {
        this.offTimeOut.set(false);
        this.hasWritten.set(true);
      }
      this.resetDebounce();
    });

    // Apagar el timeout si el campo se vuelve válido
    effect(() => {
      const val = this.valueSignal(); // forza reactividad
      const valid = this.itemControl()?.valid;
      if (valid) {
        this.offTimeOut.set(false);
      }
    });

    this.form.get('item')?.valueChanges.subscribe(() => {
      const control = this.form.get('item');
      if (!control) return;

      if (this.errorDebounceTimer) {
        clearTimeout(this.errorDebounceTimer);
      }

      if (control.value === '' && control.invalid && this.hasWritten()) {
        this.getStyleError();
        return;
      }

      if (control.valid && this.enabled()) {
        this.getStyleEnabled();
        return;
      }

      setTimeout(() => {
        // Solo si el campo sigue vacío o inválido y no se ha escrito nada
        const control = this.form.get('item');
        if (
          control &&
          (control.value === '' || control.invalid) &&
          !this.hasWritten()
        ) {
          this.offTimeOut.set(true);
          this.getStyleError();
        }
      }, this.timeoutSeconds * 1000);

      this.errorDebounceTimer = setTimeout(() => {
        if (
          control.invalid && (control.touched || control.dirty) ||
          this.offTimeOut() ||
          (control.value === '' && control.invalid && this.hasWritten())
        ) {
          this.getStyleError();
        } else if (this.enabled()) {
          this.getStyleEnabled();
        } else {
          this.getStyleTextDisabled();
        }
      }, 1700);
    });
  }

  ngOnInit() {
    
  }

  ngAfterViewInit(): void {
    this.viewInitialized.set(true);
    this.applyInitialStyles();
  }

  private applyInitialStyles() {
    if (this.enabled()) {
      this.getStyleEnabled();
    } else {
      this.getStyleTextDisabled();
    }
  }

  private safeApplyStyles(callback: () => void) {
    if (this.viewInitialized()) {
      callback();
    }
  }

  itemControl = computed(() => this.form.get('item'));

  hasError = computed(() => {
    const control = this.itemControl();
    if (!control) return false;

    const isTimedOut = this.offTimeOut();
    const isInvalidTouched = control.invalid && (control.touched || control.dirty);
    const userClearedInput = control.value === '' && control.invalid && this.hasWritten();

    return isTimedOut || isInvalidTouched || userClearedInput;
  });

  emit() {
    let valueToEmit = this.form.value.item;
    
    // Si es tipo número, eliminar las comas antes de emitir
    if (this.configSignal().type === 'number' && valueToEmit) {
      valueToEmit = valueToEmit.toString().replace(/,/g, '');
    }
    
    // Verificar si el valor es diferente al último valor emitido
    const hasValueChanged = valueToEmit !== this.lastEmittedValue();
    
    if ((this.form.valid || valueToEmit == '') && hasValueChanged) {
      this.lastEmittedValue.set(valueToEmit);
      this.submitValue.emit({formName: this.configSignal().formName , value: valueToEmit});
    }
  }

  private formatNumberWithCommas(value: string): string {
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  onNumberInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/,/g, '');
    
    if (/^\d*$/.test(value)) {
      this.form.get('item')?.setValue(value);
    } else {
      input.value = this.formattedValue();
    }
  }

  resetDebounce() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.emit();
    }, 700);
  }

  onBlurInput() {
    this.emit();
  }

  startCountdown(): Promise<boolean> {
    return new Promise((resolve) => {
      let counter = this.timeoutSeconds;
      function tick() {
        if (counter === 0) {
          resolve(true);
        } else {
          counter--;
          setTimeout(tick, 1000);
        }
      }
      tick();
    });
  }

  getStyleError(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.addClass(this.inbox.nativeElement, 'border-error')
      }
    });
  }
  
  getStyleEnabled(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.addClass(this.inbox.nativeElement, 'border-enabled')
      }
    });
  }

  getStyleTextDisabled(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.addClass(this.inbox.nativeElement, 'border-disabled')
      }
    });
  }
}